import { Polygon } from 'geojson'
import { MapLayer, DrawArea } from '@app/redux/riskMap/riskMapSlice'
import { MultiRiskDataObject, RiskData } from './RiskContext.types'
import { VisibleAssets } from '@app/components/Molecules/MapView/RiskMapView/RiskMapView.utilities'
import apiClient from '@app/config/axios.config'

export const fetchIdentifyingInformationAssetExposureStats = async (
  assetLayers: MapLayer[],
  hazardLayers: MapLayer[],
  attribute: string,
  regions: string[],
  drawAreas: DrawArea[],
  visibleAssets?: VisibleAssets,
  abortController?: AbortController,
): Promise<RiskData[]> => {
  const assetLayerTypes = assetLayers.map((assetLayer) => assetLayer.type)
  const hazardLayerAssetTags = hazardLayers.map((hazardLayer) => hazardLayer.assetTag)
  const query = [`attribute=${attribute}`]
  if (regions.length > 0) query.push(`regions=${regions.join(',')}`)
  const drawAreaCoordinateArray: Polygon | null =
    drawAreas.length > 0 ? (drawAreas[0].geometry as Polygon) : null
  const drawAreaCsv = drawAreaCoordinateArray
    ? drawAreaCoordinateArray.coordinates[0].flat().join(',')
    : ''

  const mappedVisibleAssets = visibleAssets
    ? Object.keys(visibleAssets).reduce(
        (acc, assetType) => {
          acc[assetType] = Object.keys(visibleAssets[assetType])
          return acc
        },
        {} as { [key: string]: string[] },
      )
    : visibleAssets

  const url = `/asset/exposure/identifying_information_stats/${assetLayerTypes.join(
    ',',
  )}/${hazardLayerAssetTags.join(',')}/${drawAreaCsv}?${query.join('&')}`

  const response = await apiClient.post<RiskData[]>(
    url,
    {
      prefiltered_asset_ids: mappedVisibleAssets,
    },
    { signal: abortController?.signal },
  )

  return response.data.map((data) => ({ ...data, identifying_information_property: attribute }))
}

export const fetchAssetExposureStats = async (
  assetLayers: MapLayer[],
  hazardLayers: MapLayer[],
  regions: string[],
  drawAreas: DrawArea[],
  visibleAssets?: VisibleAssets,
  abortController?: AbortController,
): Promise<RiskData[]> => {
  const assetLayerTypes = assetLayers.map((assetLayer) => assetLayer.type)
  const hazardLayerAssetTags = hazardLayers.map((hazardLayer) => hazardLayer.assetTag)
  const query = []
  if (regions.length > 0) query.push(`regions=${regions.join(',')}`)
  const drawAreaCoordinateArray: Polygon | null =
    drawAreas.length > 0 ? (drawAreas[0].geometry as Polygon) : null
  const drawAreaCsv = drawAreaCoordinateArray
    ? drawAreaCoordinateArray.coordinates[0].flat().join(',')
    : ''

  const mappedVisibleAssets = visibleAssets
    ? Object.keys(visibleAssets).reduce(
        (acc, assetType) => {
          acc[assetType] = Object.keys(visibleAssets[assetType])
          return acc
        },
        {} as { [key: string]: string[] },
      )
    : visibleAssets

  const url = `/asset/exposure/stats/${assetLayerTypes.join(',')}/${hazardLayerAssetTags.join(
    ',',
  )}/${drawAreaCsv}?${query.join('&')}`

  const response = await apiClient.post<RiskData[]>(
    url,
    {
      prefiltered_asset_ids: mappedVisibleAssets,
    },
    { signal: abortController?.signal },
  )

  return response.data
}

export const fetchAssetMultiHazardExposureStats = async (
  assetLayers: MapLayer[],
  hazardLayers: MapLayer[],
  regions: string[],
  drawAreas: DrawArea[],
  abortController: AbortController,
  visibleAssets?: VisibleAssets,
): Promise<MultiRiskDataObject> => {
  const assetLayerTypes = assetLayers.map((assetLayer) => assetLayer.type)
  const hazardLayerAssetTags = hazardLayers.map((hazardLayer) => hazardLayer.assetTag)
  const query = []
  if (regions.length > 0) query.push(`regions=${regions.join(',')}`)
  const drawAreaCoordinateArray: Polygon | null =
    drawAreas.length > 0 ? (drawAreas[0].geometry as Polygon) : null
  const drawAreaCsv = drawAreaCoordinateArray
    ? drawAreaCoordinateArray.coordinates[0].flat().join(',')
    : ''

  const mappedVisibleAssets = visibleAssets
    ? Object.keys(visibleAssets).reduce(
        (acc, assetType) => {
          acc[assetType] = Object.keys(visibleAssets[assetType])
          return acc
        },
        {} as { [key: string]: string[] },
      )
    : visibleAssets

  const url = `/asset/exposure/multihazard_stats/${assetLayerTypes.join(
    ',',
  )}/${hazardLayerAssetTags.join(',')}/${drawAreaCsv}?${query.join('&')}`

  const response = await apiClient.post<MultiRiskDataObject>(
    url,
    {
      prefiltered_asset_ids: mappedVisibleAssets,
    },
    { signal: abortController.signal },
  )

  return response.data
}

export const fetchCheckIfIdentifyingInformationExists = async (
  attribute: string,
  assetLayers: MapLayer[],
): Promise<{ asset_type: string; has_identifying_information: boolean; attribute: string }[]> => {
  if (assetLayers.length === 0) return []
  try {
    const assetLayerTypes = assetLayers.map((assetLayer) => assetLayer.type)

    const url = `/asset/check_identifying_information_exists/${assetLayerTypes.join(
      ',',
    )}/?attribute=${attribute}`

    const response =
      await apiClient.post<{ asset_type: string; has_identifying_information: boolean }[]>(url)

    return response.data.map((data) => ({ ...data, attribute }))
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    return []
  }
}
